import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Linkedin, Twitter, Facebook, Instagram } from 'lucide-react';

/**
 * Footer component with comprehensive company information and links
 * Author: VB Entreprise
 * 
 * Features:
 * - Company contact information
 * - Quick navigation links
 * - Social media links
 * - Newsletter signup
 * - Legal compliance links
 */
const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email,
          leadMagnet: 'footer-newsletter',
          selectedResource: 'Newsletter Subscription'
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setEmail('');
        setTimeout(() => setIsSubmitted(false), 3000); // Reset after 3 seconds
      } else {
        throw new Error('Failed to subscribe');
      }
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      setError('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <img src="/assets/logos/logo.png" alt="VB Entreprise Logo" className="h-12 w-auto" />
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              Leading IT & Digital Marketing Agency delivering innovative solutions for enterprise clients worldwide.
            </p>
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm">
                <Phone size={16} className="text-blue-400" />
                <span>9909206443</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Mail size={16} className="text-blue-400" />
                <span>info@vbentreprise.com</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Services</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors">Web Development</Link></li>
              <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors">Mobile Apps</Link></li>
              <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors">Digital Marketing</Link></li>
              <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors">Cloud Solutions</Link></li>
              <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors">IT Consulting</Link></li>
              <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors">Managed IT</Link></li>
              <li><Link to="/white-label-agency" className="text-jordy-blue-400 hover:text-jordy-blue-300 transition-colors font-semibold">White Label Agency</Link></li>
            </ul>
          </div>

          {/* Industries */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Industries</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/industries" className="text-gray-300 hover:text-white transition-colors">Fintech</Link></li>
              <li><Link to="/industries" className="text-gray-300 hover:text-white transition-colors">Healthcare</Link></li>
              <li><Link to="/industries" className="text-gray-300 hover:text-white transition-colors">SaaS</Link></li>
              <li><Link to="/industries" className="text-gray-300 hover:text-white transition-colors">E-Commerce</Link></li>
              <li><Link to="/blog" className="text-gray-300 hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/partner" className="text-gray-300 hover:text-white transition-colors">Partner with us</Link></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Stay Updated</h3>
            <p className="text-gray-300 text-sm">
              Subscribe to our newsletter for the latest insights and industry trends.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              {error && <div className="text-red-400 text-xs">{error}</div>}
              {isSubmitted && <div className="text-green-400 text-xs">Thank you for subscribing!</div>}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                required
                disabled={isSubmitting}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            <div className="flex space-x-4 pt-2">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <Linkedin size={20} />
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <Twitter size={20} />
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <Facebook size={20} />
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                <Instagram size={20} />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-sm text-gray-400">
            © 2024 VB Entreprise. All rights reserved.
          </div>
          <div className="flex space-x-6 text-sm">
            <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/cookies" className="text-gray-400 hover:text-white transition-colors">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;